/**
 * RFC 4122 §4.3 name-based (v5) UUID generator.
 *
 * The repo otherwise only mints v4 (`crypto.randomUUID()`); v5 is needed where a
 * UUID must be DETERMINISTIC from a `(namespace, name)` pair — e.g. the synthetic
 * harness-token user id, which is derived from the conversation id so the same
 * conversation always maps to the same (never-provisioned) principal id.
 *
 * SHA-1 via the Web Crypto API (`crypto.subtle.digest`) keeps this RN-safe and
 * dependency-free, matching `crypto.ts`. It is async because WebCrypto digest is.
 * SHA-1 is used ONLY as the RFC-mandated hash for the v5 construction — it is not
 * a security primitive here (the value is a public, opaque identifier).
 */

import { bytesToUuid, setUuidVersion } from './uuid-bytes';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Parse a canonical UUID string into its 16 raw bytes. */
function uuidToBytes(uuid: string): Uint8Array {
  if (!UUID_RE.test(uuid)) {
    throw new Error(`uuidV5: namespace is not a valid UUID: '${uuid}'`);
  }
  const hex = uuid.replace(/-/g, '');
  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

/**
 * Compute the RFC 4122 v5 (SHA-1, name-based) UUID for `name` within `namespace`.
 * Deterministic: identical `(namespace, name)` always yields the same UUID.
 *
 * @param namespace canonical UUID string identifying the namespace
 * @param name      the name to hash within that namespace (UTF-8)
 */
export async function uuidV5(namespace: string, name: string): Promise<string> {
  const nsBytes = uuidToBytes(namespace);
  const nameBytes = new TextEncoder().encode(name);

  const input = new Uint8Array(nsBytes.length + nameBytes.length);
  input.set(nsBytes, 0);
  input.set(nameBytes, nsBytes.length);

  const digest = new Uint8Array(await crypto.subtle.digest('SHA-1', input as BufferSource));
  const out = digest.slice(0, 16);

  // Version (5) in the high nibble of byte 6 + the RFC variant (10xx) in byte 8.
  return bytesToUuid(setUuidVersion(out, 5));
}
